import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const CARDS = [
  { value: 1,  effect: '− 1 corona',                      qty: 4, color: '#ef4444' },
  { value: 2,  effect: '− 2 corone',                      qty: 4, color: '#ef4444' },
  { value: 3,  effect: '− 3 corone',                      qty: 4, color: '#ef4444' },
  { value: 4,  effect: 'Nessun effetto',                   qty: 4, color: '#6b7280' },
  { value: 5,  effect: 'Nessun effetto',                   qty: 4, color: '#6b7280' },
  { value: 6,  effect: 'Nessun effetto',                   qty: 4, color: '#6b7280' },
  { value: 7,  effect: '+ 1 scudo',                       qty: 4, color: '#60a5fa' },
  { value: 8,  effect: 'Dai 1 corona al giocatore precedente', qty: 4, color: '#f59e0b' },
  { value: 9,  effect: 'Dai 1 corona al giocatore successivo', qty: 4, color: '#f59e0b' },
  { value: 10, effect: '+ 1 corona',                      qty: 4, color: '#22c55e' },
];

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 style={{
      fontFamily: "'Cinzel', serif",
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: '#fbbf24',
      borderBottom: '1px solid rgba(251,191,36,0.25)',
      paddingBottom: '6px',
      marginBottom: '10px',
    }}>
      {title}
    </h3>
    {children}
  </div>
);

const Rule = ({ icon, text }) => (
  <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'flex-start' }}>
    <span style={{ fontSize: '13px', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
    <span style={{ fontSize: '12px', color: '#d1d5db', lineHeight: 1.5 }}>{text}</span>
  </div>
);

const GameRules = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* ── Info button ── */}
      <motion.button
        onClick={() => setOpen(true)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        title="Regolamento"
        style={{
          position: 'fixed',
          top: '54px',
          right: '12px',
          zIndex: 50,
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          background: 'rgba(15,15,20,0.85)',
          backdropFilter: 'blur(8px)',
          border: '2px solid rgba(100,80,30,0.6)',
          boxShadow: '0 0 12px rgba(251,191,36,0.15), 0 2px 8px rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <span style={{
          fontFamily: 'Georgia, serif',
          fontSize: '17px',
          fontStyle: 'italic',
          fontWeight: 400,
          color: '#fbbf24',
          lineHeight: 1,
          userSelect: 'none',
        }}>i</span>
      </motion.button>

      {/* ── Overlay + Panel ── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 55,
                background: 'rgba(0,0,0,0.65)',
                backdropFilter: 'blur(3px)',
              }}
            />

            {/* Panel */}
            <motion.div
              key="panel"
              initial={{ opacity: 0, y: -20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              style={{
                position: 'fixed',
                top: '60px',
                right: '12px',
                zIndex: 60,
                width: 'min(420px, calc(100vw - 24px))',
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                borderRadius: '16px',
                background: 'linear-gradient(160deg, #0f1018 0%, #141620 60%, #0c0e14 100%)',
                border: '1px solid rgba(251,191,36,0.25)',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.8), 0 24px 60px rgba(0,0,0,0.7), 0 0 40px rgba(251,191,36,0.06)',
                padding: '20px',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
                <h2 style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: '16px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  background: 'linear-gradient(to bottom, #FFEC80, #FFD700, #B8860B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0,
                }}>
                  ♛ L'Ultimo Re — Regolamento
                </h2>
                <motion.button
                  onClick={() => setOpen(false)}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    padding: '4px',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    display: 'flex',
                  }}
                >
                  <X size={16} />
                </motion.button>
              </div>

              {/* ── Obiettivo ── */}
              <Section title="Obiettivo">
                <Rule icon="👑" text="Essere l'unico giocatore con almeno una corona, mentre tutti gli altri sono imprigionati con 0 corone." />
              </Section>

              {/* ── Setup ── */}
              <Section title="Setup iniziale">
                <Rule icon="♛" text="Ogni giocatore parte con 3 corone." />
                <Rule icon="🛡" text="Ogni giocatore parte con 0 scudi. Gli scudi proteggono dalle carte 1, 2 e 3, ma non dalle carte 8 e 9." />
                <Rule icon="🃏" text="Ogni giocatore parte con 2 jolly (massimo 2). Usare un jolly fa saltare il proprio turno senza effetti sulle carte." />
              </Section>

              {/* ── Turni ── */}
              <Section title="Turni di gioco">
                <Rule icon="🃏" text="Ogni turno inizia con 4 carte coperte sul tavolo. I giocatori vedono i valori disponibili ma non la disposizione." />
                <Rule icon="↩️" text="I giocatori giocano in senso antiorario." />
                <Rule icon="✋" text="Ogni giocatore può fare una sola azione per turno: scegliere una carta oppure usare un jolly." />
                <Rule icon="🔄" text="Quando tutti i giocatori attivi hanno agito, si passa al round successivo con altre 4 carte coperte." />
                <Rule icon="♻️" text="Se il mazzo si esaurisce con almeno due giocatori attivi, si ricomincia con un nuovo mazzo da 40 carte." />
              </Section>

              {/* ── Imprigionamento ── */}
              <Section title="Imprigionamento">
                <Rule icon="⛓" text="Un giocatore con 0 corone è imprigionato e salta il turno." />
                <Rule icon="🔓" text="Può tornare in gioco ricevendo 1 corona: dal giocatore precedente che pesca un 9, o dal giocatore successivo che pesca un 8." />
              </Section>

              {/* ── Tabella carte ── */}
              <Section title="Valore delle carte (40 carte totali)">
                <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 32px', gap: '4px 10px', alignItems: 'center' }}>
                  {/* Header */}
                  <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Carta</span>
                  <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Effetto</span>
                  <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' }}>Q.tà</span>

                  {CARDS.map(({ value, effect, qty, color }) => (
                    <>
                      {/* Card number badge */}
                      <div key={`v${value}`} style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1a1c28, #12141e)',
                        border: `1px solid ${color}55`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 900,
                        fontSize: '14px',
                        color,
                        boxShadow: `0 0 8px ${color}22`,
                      }}>
                        {value}
                      </div>
                      {/* Effect */}
                      <span key={`e${value}`} style={{ fontSize: '12px', color: '#d1d5db', lineHeight: 1.4 }}>{effect}</span>
                      {/* Qty */}
                      <span key={`q${value}`} style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', fontWeight: 600 }}>×{qty}</span>
                    </>
                  ))}
                </div>
              </Section>

              {/* Footer note */}
              <div style={{
                marginTop: '4px',
                padding: '10px 14px',
                borderRadius: '10px',
                background: 'rgba(251,191,36,0.06)',
                border: '1px solid rgba(251,191,36,0.15)',
                fontSize: '11px',
                color: '#9ca3af',
                lineHeight: 1.5,
              }}>
                💡 <strong style={{ color: '#fbbf24' }}>Ricorda:</strong> gli scudi proteggono solo dalle carte 1, 2 e 3. Le carte 8 e 9 trasferiscono corone indipendentemente dagli scudi.
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default GameRules;
